import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { useAsyncData } from '@/hooks';
import { pipelineService } from '@/services/crm/pipelineService';
import type { PipelineCase, PipelineStage } from '@/types/pipeline';
import { ROUTES } from '@/constants/routes';
import {
  ArrowLeftIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChartBarSquareIcon,
} from '@heroicons/react/24/outline';

const formatCurrency = (value: number | null | undefined, currency = 'SEK') => {
  if (value == null) return '–';
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
};

export default function SalesPipelinePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeFilter = searchParams.get('type') || '';
  const [search, setSearch] = useState('');

  const params: Record<string, string> = {};
  if (typeFilter === 'inquiry' || typeFilter === 'procurement') params.case_type = typeFilter;
  if (search) params.search = search;

  const { data: stages, loading: stagesLoading } = useAsyncData<PipelineStage[]>(
    '/api/crm/pipeline/stages',
    { initialData: [] }
  );

  const { data: cases, loading: casesLoading, error: casesError, refetch } = useAsyncData<PipelineCase[]>(
    '/api/crm/pipeline/cases',
    { params, deps: [typeFilter, search], initialData: [], transform: (d) => d?.cases || [] }
  );

  const casesByStage = useMemo(() => {
    const map = new Map<number, PipelineCase[]>();
    for (const stage of stages || []) map.set(stage.id, []);
    for (const c of cases || []) {
      const list = map.get(c.stage_id) || [];
      list.push(c);
      map.set(c.stage_id, list);
    }
    return map;
  }, [stages, cases]);

  const handleDrop = useCallback(async (caseId: number, stageId: number) => {
    await pipelineService.updateStage(caseId, stageId);
    refetch();
  }, [refetch]);

  const onDragStart = (e: React.DragEvent, caseId: number) => {
    e.dataTransfer.setData('caseId', String(caseId));
  };

  const onDrop = (e: React.DragEvent, stageId: number) => {
    e.preventDefault();
    const caseId = parseInt(e.dataTransfer.getData('caseId'));
    if (caseId) handleDrop(caseId, stageId);
  };

  return (
    <div className="p-4 bg-bgContainer h-full overflow-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(ROUTES.CRM)} className="p-2 hover:bg-bgElevated rounded-lg">
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
          </button>
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <ChartBarSquareIcon className="w-6 h-6 text-primary" />
            {t('crm.pipeline')}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-textTertiary" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('common.search')}
              className="pl-9 pr-3 py-2 rounded-lg border border-border bg-bgElevated text-sm"
            />
          </div>
          <Link
            to={`${ROUTES.CRM_PIPELINE}/new${typeFilter ? `?type=${typeFilter}` : ''}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90"
          >
            <PlusIcon className="w-4 h-4" />
            {t('pipeline.newCase')}
          </Link>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {[
          { key: '', label: t('pipeline.filterAll') },
          { key: 'inquiry', label: t('pipeline.typeInquiry') },
          { key: 'procurement', label: t('pipeline.typeProcurement') },
        ].map((f) => (
          <Link
            key={f.key || 'all'}
            to={f.key ? `${ROUTES.CRM_PIPELINE}?type=${f.key}` : ROUTES.CRM_PIPELINE}
            className={`px-3 py-1.5 rounded-full text-sm border ${
              typeFilter === f.key ? 'bg-primary text-white border-primary' : 'border-border text-textSecondary'
            }`}
          >
            {f.label}
          </Link>
        ))}
      </div>

      {(stagesLoading || casesLoading) && (
        <p className="text-textSecondary">{t('common.loading')}</p>
      )}

      {casesError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
          {casesError.message}
        </div>
      )}

      <div className="flex gap-4 overflow-x-auto pb-4 min-h-[60vh]">
        {(stages || []).map((stage) => (
          <div
            key={stage.id}
            className="min-w-[280px] max-w-[320px] flex-shrink-0 bg-bgElevated rounded-xl border border-border flex flex-col"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDrop(e, stage.id)}
          >
            <div className="p-3 border-b border-border flex items-center justify-between">
              <div>
                <div className="font-semibold text-textDefault flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color || '#6366f1' }} />
                  {stage.name}
                </div>
                <div className="text-xs text-textTertiary">{(casesByStage.get(stage.id) || []).length} cases</div>
              </div>
            </div>
            <div className="p-2 flex-1 space-y-2 overflow-y-auto max-h-[70vh]">
              {(casesByStage.get(stage.id) || []).map((c) => (
                <div
                  key={c.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, c.id)}
                  onClick={() => navigate(`${ROUTES.CRM_PIPELINE}/${c.id}`)}
                  className="p-3 bg-bgContainer rounded-lg border border-border cursor-pointer hover:border-primary/50"
                >
                  <div className="font-medium text-textDefault text-sm mb-1">{c.title}</div>
                  <div className="text-xs text-textTertiary mb-2">
                    {c.case_type === 'procurement' ? t('pipeline.typeProcurement') : t('pipeline.typeInquiry')}
                    {c.customer_name ? ` · ${c.customer_name}` : ''}
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-textSecondary">{formatCurrency(c.calculated_value || c.estimated_value, c.currency)}</span>
                    {c.submission_deadline && (
                      <span className="text-orange-600">{new Date(c.submission_deadline).toLocaleDateString('sv-SE')}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
